/**
 * Where the large runtime assets come from.
 *
 * Models, atlases, the ONNX Runtime wasm binaries and the example subjects
 * are not shipped with the site: they are fetched on first use from the
 * asset repository (pinned to one commit, so results never change under the
 * app) and kept in the browser's Cache Storage after that. Code that loads
 * them keeps using the familiar local paths (`/models/...`, `/atlases/...`,
 * `/demo/...`, `/ort/*.wasm`); `fetchAsset` maps them. A cached copy is
 * reused for 7 days, then downloaded again.
 *
 * The base is fixed at build time (`ADS_ASSET_BASE`, see vite.config.ts and
 * scripts/build-workers.mjs). `ADS_ASSET_BASE=local` serves everything from
 * `public/` instead, for offline work.
 */

declare const __ADS_ASSET_BASE__: string | undefined;
declare const __ADS_APP_BASE__: string | undefined;

/**
 * Where this app itself is served from ("/" normally; "/Software/openads/"
 * inside the wavnc desktop). Same-origin files — worker scripts, ORT's .mjs
 * loader, the ANTs builds — are addressed through `appPath`.
 */
export const APP_BASE: string = typeof __ADS_APP_BASE__ === "string" ? __ADS_APP_BASE__.replace(/\/*$/, "/") : "/";

export function appPath(path: string): string {
  return APP_BASE + path.replace(/^\/+/, "");
}

/** Remote base (no trailing slash), or "" for same-origin `public/` files. */
export const ASSET_BASE: string = typeof __ADS_ASSET_BASE__ === "string" ? __ADS_ASSET_BASE__.replace(/\/+$/, "") : "";

/** Local `public/` path -> path inside the asset repository. */
const REMOTE_LAYOUT: [RegExp, string][] = [
  [/^\/models\//, "models/"],
  [/^\/atlases\//, "atlases/JHU_ICBM/"],
  [/^\/demo\/raw-pwi\//, "examples/pwi/sub-02e8eb42/"],
  [/^\/demo\/raw-dwi\//, "examples/dwi/sub-02e8eb42/"],
  [/^\/ort\/(?=[^/]+\.wasm$)/, "ort/"],
];

export function assetUrl(path: string): string {
  if (!ASSET_BASE) return appPath(path);
  for (const [re, remote] of REMOTE_LAYOUT) {
    if (re.test(path)) return `${ASSET_BASE}/${path.replace(re, remote)}`;
  }
  return path;
}

const CACHE_NAME = `ads-assets:${ASSET_BASE || "local"}`;

/** Downloaded assets are reused for this long, then fetched again once. */
const CACHE_MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000;
const CACHED_AT = "x-ads-cached-at";

/**
 * `fetch` for an asset path, served from Cache Storage for 7 days after a
 * download. Paths the asset repository does not hold pass straight through.
 */
export async function fetchAsset(path: string): Promise<Response> {
  const url = assetUrl(path);
  if (!ASSET_BASE || url === path || typeof caches === "undefined") return fetch(url);
  try {
    const cache = await caches.open(CACHE_NAME);
    const hit = await cache.match(url);
    if (hit && Date.now() - Number(hit.headers.get(CACHED_AT) ?? 0) < CACHE_MAX_AGE_MS) return hit;
    const res = await fetch(url);
    if (!res.ok) return hit ?? res; // offline or remote down: an expired copy still beats nothing
    const blob = await res.blob();
    const headers = new Headers(res.headers);
    headers.set(CACHED_AT, String(Date.now()));
    await cache.put(url, new Response(blob, { status: 200, headers }));
    return new Response(blob, { status: 200, headers });
  } catch {
    // Storage blocked (private window, quota): still serve the file.
    return fetch(url);
  }
}

/**
 * The ONNX Runtime wasm binary, handed to ORT as bytes (`env.wasm.wasmBinary`)
 * so it can come from the asset repository while ORT's small `.mjs` loader
 * keeps loading from this site (module scripts must be same-origin-typed).
 */
export async function ortWasmBinary(file: "ort-wasm-simd-threaded.wasm" | "ort-wasm-simd-threaded.asyncify.wasm"): Promise<ArrayBuffer> {
  const res = await fetchAsset(`/ort/${file}`);
  if (!res.ok) throw new Error(`/ort/${file} -> ${res.status}`);
  return res.arrayBuffer();
}
